import { z } from "zod";
import { providerProductAreaSchema, productAreaCensusSchema } from "@/lib/tenant-inventory-contract";
import {
  activationPreviewSchema,
  entitlementResponseSchema,
  productAreaIdSchema,
  productAreaListSchema,
  productAreaSchema,
  productAreaStateSchema,
  runtimeEffectSchema,
  setupPreviewSchema,
  setupTransitionSchema,
} from "./api.generated";

// Response shapes come from the published contract (api/openapi/admin.yaml).
// productAreaIdSchema stays in lockstep with productarea.Areas() in
// backend/internal/tenant/productarea; TestConsoleKnowsEveryProductArea there
// holds the two together, because an area missing here fails the whole
// inventory parse and every area gate reads unknown.
//
// runtimeEffectSchema is what an area's switch does to sign-in and access at
// runtime, shared by every reader of a product-area decision.
//
// The previews are the server's decision plus the facts behind it. The client
// renders them and never re-derives eligibility: an unrecognized blocker must
// still block, so blockers stay open strings rather than a client-side enum.
export {
  activationPreviewSchema,
  entitlementResponseSchema,
  productAreaCensusSchema,
  productAreaIdSchema,
  productAreaListSchema,
  productAreaSchema,
  providerProductAreaSchema,
  runtimeEffectSchema,
  setupPreviewSchema,
  setupTransitionSchema,
};

// Setup is a separate axis from activation. An area can be in progress while
// activation stays off; the two must never be collapsed into one status.
export const setupStateSchema = productAreaStateSchema.shape.setup;

export const automatedOnboardingCapabilitySchema = z.object({
  data: z.object({
    capability: z.literal("automated_onboarding"),
    availability: z.enum(["hidden", "read_only", "manage"]),
    reason_code: z.string().min(1),
    runtime_effect: runtimeEffectSchema,
  }),
});

export const applicationCreationCapabilitySchema = z.object({
  data: z.object({
    capability: z.literal("application_creation"),
    availability: z.enum(["hidden", "read_only", "manage"]),
    reason_code: z.string().min(1),
    runtime_effect: runtimeEffectSchema,
  }),
});

export type ProductArea = z.infer<typeof productAreaSchema>;
export type ProviderProductArea = z.infer<typeof providerProductAreaSchema>;
export type EntitlementResponse = z.infer<typeof entitlementResponseSchema>;
export type SetupPreview = z.infer<typeof setupPreviewSchema>;
export type ActivationPreview = z.infer<typeof activationPreviewSchema>;
export type SetupTransition = z.infer<typeof setupTransitionSchema>;
export type ProductAreaState = z.infer<typeof productAreaStateSchema>;
